import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { isValidSurplusQuantity, MAX_SURPLUS_PLAYS, MIN_SURPLUS_PLAYS, parseSurplusQuantity } from '@/utils/surplusPricing';

interface Props {
  quantity: number;
  onChange: (quantity: number) => void;
  onIncrement: () => void;
  onDecrement: () => void;
  disabled: boolean;
}

export function SurplusQuantitySelector({ quantity, onChange, onIncrement, onDecrement, disabled }: Props) {
  // Cada edição precisa de um commit para restaurar o cursor, mesmo se só apagar um ponto.
  const [draft, setDraft] = useState(() => ({ text: quantity.toLocaleString('pt-BR') }));
  const editing = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const nextCaret = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (nextCaret.current === null) return;
    inputRef.current?.setSelectionRange(nextCaret.current, nextCaret.current);
    nextCaret.current = null;
  });
  const hintId = useId();
  const valid = isValidSurplusQuantity(quantity);
  useEffect(() => {
    if (!editing.current || disabled) setDraft({ text: Number.isFinite(quantity) ? quantity.toLocaleString('pt-BR') : '' });
  }, [quantity, disabled]);
  const buttonClass = 'w-9 h-full flex items-center justify-center text-[#6B7280] hover:bg-[#F3F4F6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#2563EB] transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
  return <div className="text-center">
    <div className="inline-flex h-11 items-center overflow-hidden rounded-[10px] border border-[#E5E7EB] focus-within:border-[#2563EB] focus-within:ring-2 focus-within:ring-[#2563EB]/15">
      <button type="button" aria-label="Diminuir plays" onClick={() => { editing.current = false; onDecrement(); }} disabled={disabled || quantity <= MIN_SURPLUS_PLAYS} className={buttonClass}>
        <Minus className="h-3.5 w-3.5" />
      </button>
      <input ref={inputRef} type="text" inputMode="numeric" role="spinbutton" aria-label="Quantidade de plays"
        aria-valuemin={MIN_SURPLUS_PLAYS} aria-valuemax={MAX_SURPLUS_PLAYS}
        aria-valuenow={Number.isFinite(quantity) ? quantity : undefined}
        aria-invalid={!valid} aria-describedby={hintId} autoComplete="off"
        value={draft.text} disabled={disabled}
        onFocus={() => { editing.current = true; }}
        onBlur={() => {
          editing.current = false;
          if (Number.isFinite(quantity)) setDraft({ text: quantity.toLocaleString('pt-BR') });
        }}
        onChange={event => {
          editing.current = true;
          const raw = event.target.value;
          const digits = raw.replaceAll('.', '');
          const parsed = parseSurplusQuantity(digits);
          const formatted = Number.isSafeInteger(parsed) ? parsed.toLocaleString('pt-BR') : raw;
          if (/^\d+$/.test(digits)) {
            const digitsBeforeCaret = raw.slice(0, event.target.selectionStart ?? raw.length).replace(/\D/g, '').length;
            let seen = 0;
            let position = 0;
            while (position < formatted.length && seen < digitsBeforeCaret) {
              if (/\d/.test(formatted[position])) seen++;
              position++;
            }
            nextCaret.current = position;
          }
          setDraft({ text: formatted });
          onChange(parsed);
        }}
        onKeyDown={event => {
          if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
          event.preventDefault();
          editing.current = false;
          if (event.key === 'ArrowUp') onIncrement(); else onDecrement();
        }}
        className="h-full w-[148px] border-0 bg-transparent px-2 text-center text-base font-semibold tabular-nums text-[#111827] outline-none disabled:cursor-not-allowed disabled:opacity-60" />
      <button type="button" aria-label="Aumentar plays" onClick={() => { editing.current = false; onIncrement(); }} disabled={disabled || quantity >= MAX_SURPLUS_PLAYS} className={buttonClass}>
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
    <p id={hintId} role={valid ? undefined : 'alert'} className={`mt-2 text-xs ${valid ? 'text-[#6B7280]' : 'text-red-700'}`}>
      {valid ? 'Digite a quantidade desejada. Mínimo de 1.000 plays.' : 'Informe uma quantidade inteira entre 1.000 e 99.999.999 plays.'}
    </p>
  </div>;
}
